import { ArrowRight, ShieldCheck, Sparkles } from "lucide-react";
import { motion } from "framer-motion";
import PhoneMockup from "@/components/PhoneMockup";
import { useI18n } from "@/lib/i18n";

export default function Hero() {
  const { t } = useI18n();

  const scroll = (h) => {
    const el = document.querySelector(h);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section id="home" className="relative pt-28 md:pt-32 pb-20 md:pb-28 overflow-hidden">
      {/* Background layers */}
      <div className="absolute inset-0 -z-10 bg-white" />
      <div className="absolute inset-0 -z-10 dot-pattern opacity-60" />
      <div className="absolute -z-10 top-0 end-0 h-[520px] w-[520px] rounded-full bg-brand-greenSoft blur-3xl opacity-70" />
      <div className="absolute -z-10 bottom-0 start-0 h-[380px] w-[380px] rounded-full bg-brand-goldSoft blur-3xl opacity-60" />

      <div className="container-x grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-7">
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="inline-flex items-center gap-2 rounded-full border border-brand-line bg-white/80 backdrop-blur px-3 py-1.5 text-xs font-semibold text-brand-charcoal/70"
            data-testid="hero-badge"
          >
            <Sparkles className="h-3.5 w-3.5 text-brand-gold" />
            {t("hero.badge")}
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.05 }}
            className="h1 mt-5"
            data-testid="hero-title"
          >
            {t("hero.title")}
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.12 }}
            className="lede mt-5 max-w-2xl"
          >
            {t("hero.subtitle")}
          </motion.p>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="mt-6 flex items-center gap-2"
          >
            <div className="h-px w-10 bg-brand-charcoal/30" />
            <div className="text-sm font-semibold tracking-[0.24em] uppercase text-brand-charcoal">
              {t("hero.tagline")}
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.28 }}
            className="mt-8 flex flex-wrap items-center gap-3"
          >
            <button
              onClick={() => scroll("#early-access")}
              className="btn-primary group"
              data-testid="hero-cta-early"
            >
              {t("hero.cta_primary")}
              <ArrowRight className="h-4 w-4 rtl:rotate-180 transition-transform duration-200 group-hover:translate-x-0.5" />
            </button>
            <button
              onClick={() => scroll("#partners")}
              className="btn-outline"
              data-testid="hero-cta-pilot"
            >
              {t("hero.cta_secondary")}
            </button>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="mt-8 flex items-center gap-2 text-sm text-brand-charcoal/70"
          >
            <ShieldCheck className="h-4 w-4 text-brand-green" />
            {t("hero.trust")}
          </motion.div>
        </div>

        <div className="lg:col-span-5 flex justify-center lg:justify-end">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.15 }}
          >
            <PhoneMockup />
          </motion.div>
        </div>
      </div>
    </section>
  );
}
